import {
  BidId,
  MakerBidAcceptedByBrandReasons,
  MakerBidRejectedByBrandReasons,
  OpportunityToBidAcceptedByMakerReasons,
  OpportunityToBidDeclinedByMakerReasons,
  ProjectId,
  type SurveyDomainSelectModel,
  SurveyId,
  SurveyTypes,
  TeamId,
  UserId,
} from '@mntn-dev/domain-types'
import {
  getRandomItem,
  makeRange,
  mapNonEmptyArray,
  UnhandledUnionError,
} from '@mntn-dev/utilities'

import { MockModel } from './mock-model.ts'

const getRandomRating = () =>
  getRandomItem(mapNonEmptyArray(makeRange(5), (i) => i + 1))

export const MockSurveyDetails = (): SurveyDomainSelectModel['details'] => {
  const surveyType = getRandomItem(SurveyTypes)

  switch (surveyType) {
    case 'maker-bid-accepted-by-brand': {
      const bidId = BidId()

      return {
        surveyType,
        bidId,
        responses: {
          reason: getRandomItem(MakerBidAcceptedByBrandReasons),
          feedback: `Feedback for ${bidId} ${surveyType}`,
        },
      }
    }

    case 'maker-bid-rejected-by-brand': {
      const bidId = BidId()

      return {
        surveyType,
        bidId,
        responses: {
          reason: getRandomItem(MakerBidRejectedByBrandReasons),
          feedback: `Feedback survey ${bidId} ${surveyType}`,
        },
      }
    }

    case 'opportunity-to-bid-accepted-by-maker': {
      const bidId = BidId()

      return {
        surveyType,
        bidId,
        responses: {
          reason: getRandomItem(OpportunityToBidAcceptedByMakerReasons),
          feedback: `Feedback for ${bidId} ${surveyType}`,
        },
      }
    }

    case 'opportunity-to-bid-declined-by-maker': {
      const bidId = BidId()

      return {
        surveyType,
        bidId,
        responses: {
          reason: getRandomItem(OpportunityToBidDeclinedByMakerReasons),
          feedback: `Feedback survey ${bidId} ${surveyType}`,
        },
      }
    }

    case 'legacy-quickframe-maker-rating': {
      return {
        surveyType,
        agencyTeamId: TeamId(),
        responses: {
          projectsRated: 1,
          experienceRating: getRandomRating(),
          qualityRating: getRandomRating(),
        },
      }
    }

    case 'project-completed-brand-reviews-maker': {
      const projectId = ProjectId()

      return {
        surveyType,
        projectId,
        responses: {
          finalVideoQualityRating: getRandomRating(),
          makerRating: getRandomRating(),
          feedback: `Feedback for ${projectId} ${surveyType}`,
        },
      }
    }

    case 'project-completed-maker-reviews-brand': {
      const projectId = ProjectId()

      return {
        surveyType,
        projectId,
        responses: {
          brandRating: getRandomRating(),
          feedback: `Feedback for ${projectId} ${surveyType}`,
        },
      }
    }

    default: {
      throw new UnhandledUnionError(surveyType)
    }
  }
}

export const MockSurvey = MockModel<SurveyDomainSelectModel>(
  ({ surveyId = SurveyId(), details = MockSurveyDetails() }) => {
    return {
      surveyId,
      responderId: UserId(),
      details,
    } satisfies SurveyDomainSelectModel
  }
)
